import {
  Modal,
  Select,
  Form as AForm,
  Flex,
  Input,
  Radio,
  Typography,
  FormInstance,
  Button as AButton,
} from 'antd';
import React, { useState } from 'react';
import { Wizard as Stepper } from 'react-use-wizard';
import { CURRENCY_TYPES, NETWORK_TYPES, TOKEN_TYPES } from '../bill/lib';
import { useCurrentUserDetails } from '../../entities/user/model';
import { usePaymentDetails } from '../../entities/organization';
import { PencilIcon } from '@heroicons/react/24/outline';
import { useCreateTransfer } from './useCreateTransfer';
import { toast } from 'react-toastify';
import { useTransfers } from './useTransfers';
import { PaymentDetailsTypes } from '../payments/paymentDetails/PaymentDetailsTypes';
import { ethereumWalletRegex } from '../../shared/lib/string';
import { countries } from '../../shared/ui/form/CountrySelect';
import isoCountries from 'i18n-iso-countries';
import { useAuth0 } from '@auth0/auth0-react';
import { sendTransactionLog } from '../analytics/sendTransactionLog';

const { Text } = Typography;

export const AVAILABLE_NETWORKS_BY_CURRENCIES = {
  [TOKEN_TYPES.USDC]: [
    NETWORK_TYPES.Ethereum,
    NETWORK_TYPES.Polygon,
    NETWORK_TYPES.Base,
  ],
  [TOKEN_TYPES.USDT]: [
    NETWORK_TYPES.Ethereum,
    NETWORK_TYPES.Polygon,
    NETWORK_TYPES.Base,
  ],
  [CURRENCY_TYPES.USD]: ['fiat'],
  [CURRENCY_TYPES.EUR]: ['fiat'],
};

export const AddNewTransferModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onTransferCreated: (transfer: Record<string, any>) => void;
  transferCandidate: {
    sourceCurrency: string;
    sourceCurrencySymbol: string;
    sourceNetwork: string;
    sourceAmount: string;
    destinationCurrency: string;
    destinationCurrencySymbol: string;
    destinationNetwork: string;
    destinationAmount: string;
  };
}> = ({ show, onClose, onTransferCreated, transferCandidate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  const { isPending, createTransfer } = useCreateTransfer();
  const { invalidate } = useTransfers();
  const [transferData, setTransferData] = useState<Record<string, any>>({});

  const _handleFinishStep = (
    partialFormData: Record<string, any>,
    isSubmit: boolean
  ) => {
    const formData = {
      ...transferData,
      ...partialFormData,
    };

    setTransferData(formData);

    if (isSubmit) {
      createTransfer({
        destinationPaymentDetails: formData.destinationPaymentDetails,
        destinationPaymentDetailsMeta: formData.destinationPaymentDetailsMeta,
        recipientAddress: formData.recipientAddress,

        organizationId: getCurrentOrganizationId(),
        destinationNetwork: transferCandidate.destinationNetwork.toLowerCase(),
        transferAmount: parseFloat(transferCandidate.destinationAmount),
        sourceCurrency: transferCandidate.sourceCurrency,
        sourceNetwork: transferCandidate.sourceNetwork.toLowerCase(),
        destinationCurrency: transferCandidate.destinationCurrency,
      })
        .then((transfer) => {
          onTransferCreated(transfer);
          onClose();
          return invalidate();
        })
        .catch((err) => {
          sendTransactionLog(getAccessTokenSilently, {
            title: '[FAIL][ADD NEW TRANSFER]',
            err,
          });
          toast.error(
            `Oops, something went wrong, contact support: pavel@getfractal.xyz`
          );
        });
    }
  };

  return (
    <Modal
      title="Initiate Transfer"
      open={show}
      maskClosable={false}
      footer={null}
      centered
      destroyOnClose
      loading={isPending}
      onCancel={onClose}>
      <Stepper>
        <RecipientPaymentDetailsStep
          transferCandidate={transferCandidate}
          onFinishStep={_handleFinishStep}
          onPrevStep={onClose}
        />
      </Stepper>
    </Modal>
  );
};

const RecipientPaymentDetailsStep = ({
  transferCandidate,
  onFinishStep,
  onPrevStep,
}: {
  transferCandidate: {
    sourceCurrency: string;
    sourceCurrencySymbol: string;
    sourceNetwork: string;
    sourceAmount: string;
    destinationCurrency: string;
    destinationCurrencySymbol: string;
    destinationNetwork: string;
    destinationAmount: string;
  };
  onFinishStep: (
    partialFormData: Record<string, any>,
    isSubmit: boolean
  ) => void;
  onPreSaveData?: () => void;
  onPrevStep?: () => void;
}) => {
  const { data: paymentDetailsData, isPending } = usePaymentDetails();
  const [form] = AForm.useForm();

  const _getPaymentDetailsList = () => {
    if (transferCandidate.destinationNetwork === 'fiat') {
      return (paymentDetailsData?.result || []).filter(
        (p: any) => p.type === 'bank'
      );
    }

    return (paymentDetailsData?.result || []).filter(
      (p: any) =>
        p.type === 'crypto_wallet' || p.type === 'fractal_abstract_bank_account'
    );
  };

  const _handlePrev = () => {
    onPrevStep();
  };

  const _createOuterFormData = (localFormData: Record<string, any>) => {
    const result: Record<string, any> = {};
    const pds = _findPdsById(localFormData.destinationPaymentDetailsId);

    if (transferCandidate.destinationNetwork === 'fiat') {
      if (pds && pds.details.bankAccount) {
        result.destinationPaymentDetails = {
          type: PaymentDetailsTypes.BankAccount,
          accountBeneficiary:
            localFormData.accountBeneficiary ||
            pds.details.bankAccount.accountBeneficiary,
          bankName: localFormData.bankName || pds.details.bankAccount.bankName,
          bankAddress: pds.details.bankAccount.bankAddress,
          bankAccountBeneficiaryType:
            localFormData.bankAccountBeneficiaryType ||
            pds.details.bankAccount.bankAccountBeneficiaryType,
          accountNumber:
            localFormData.accountNumber ||
            pds.details.bankAccount.accountNumber,
          accountACHRoutingNumber:
            localFormData.accountACHRoutingNumber ||
            pds.details.bankAccount.accountACHRoutingNumber,
          accountIBAN:
            localFormData.accountIBAN || pds.details.bankAccount.accountIBAN,
          bankBIC: localFormData.bankBIC || pds.details.bankAccount.bankBIC,
        };
        result.destinationPaymentDetailsMeta = {
          pdsType: pds.type,
          businessRelation: pds.businessRelation,
          pdsId: pds.id,
          pdsName: pds.name,
        };
      } else {
        result.destinationPaymentDetails = {
          type: PaymentDetailsTypes.BankAccount,
          accountBeneficiary: localFormData.accountBeneficiary,
          bankName: localFormData.bankName,
          bankAccountBeneficiaryType: localFormData.bankAccountBeneficiaryType,
          accountNumber: localFormData.accountNumber,
          accountACHRoutingNumber: localFormData.accountACHRoutingNumber,
          accountIBAN: localFormData.accountIBAN,
          bankBIC: localFormData.bankBIC,
        };
      }
      result.destinationPaymentDetails.bankAddressObj = {
        countryAlpha3: isoCountries.alpha2ToAlpha3(
          localFormData['bankAddressObj.countryAlpha2']
        ),
        countryAlpha2: localFormData['bankAddressObj.countryAlpha2'],
      };
      result.recipientAddress = {
        streetAddress: localFormData['recipientAddress.streetAddress'],
        postalCode: localFormData['recipientAddress.postalCode'],
        countryAlpha2: localFormData['recipientAddress.countryAlpha2'],
        city: localFormData['recipientAddress.city'],
      };
    } else {
      if (pds) {
        result.destinationPaymentDetails = {
          address:
            localFormData.walletAddress || pds.details.cryptoWallet.address,
          type: PaymentDetailsTypes.CryptoWallet,
        };
        result.destinationPaymentDetailsMeta = {
          pdsType: pds.type,
          businessRelation: pds.businessRelation,
          pdsId: pds.id,
          pdsName: pds.name,
        };
      } else {
        result.destinationPaymentDetails = {
          address: localFormData.walletAddress,
          type: PaymentDetailsTypes.CryptoWallet,
        };
      }
    }

    return result;
  };

  const _handleNext = () => {
    form
      .validateFields({ validateOnly: false })
      .then(() => {
        onFinishStep(_createOuterFormData(form.getFieldsValue()), true);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const _findPdsById = (id: string) => {
    return _getPaymentDetailsList().find(
      (pds: Record<string, any>) => pds.id === id
    );
  };

  const _handleRecipientChange = (value: any) => {
    const values: Record<string, any> = {
      destinationPaymentDetailsId: value,
    };

    /** Prefill bank account data */
    if (transferCandidate.destinationNetwork && value !== 'manual') {
      const pds = _findPdsById(value);
      if (pds && pds.details.bankAccount) {
        const bankAccount = pds.details.bankAccount;
        values.accountBeneficiary = bankAccount.accountBeneficiary;
        values.bankBIC = bankAccount.bankBIC;
        values.accountACHRoutingNumber = bankAccount.accountACHRoutingNumber;
        values.accountIBAN = bankAccount.accountIBAN;
        values.accountNumber = bankAccount.accountNumber;
        values.bankName = bankAccount.bankName;
        values.bankAccountBeneficiaryType =
          bankAccount.bankAccountBeneficiaryType;
        values['bankAddressObj.countryAlpha2'] =
          bankAccount?.bankAddressObj?.countryAlpha2;

        if (bankAccount.accountIBAN) {
          values.bankTransferMethod = 'iban';
        } else {
          values.bankTransferMethod = 'ach';
        }
      }
    }

    form.setFieldsValue(values);
  };

  return (
    <AForm
      name="recipient"
      form={form}
      requiredMark={false}
      style={{
        paddingTop: 20,
      }}
      initialValues={{
        walletAddress: '',
        accountBeneficiary: '',
        bankTransferMethod:
          transferCandidate.destinationCurrencySymbol === 'usd'
            ? 'ach'
            : 'iban',
      }}
      layout="vertical">
      <AForm.Item
        rules={[
          {
            required: true,
            message: 'Please select recipient',
          },
        ]}
        name="destinationPaymentDetailsId"
        layout="vertical"
        label="Select Recipient Payment Details">
        <Select
          placeholder={'--'}
          loading={isPending}
          onChange={_handleRecipientChange}
          options={[
            {
              value: 'manual',
              label: (
                <Flex>
                  <PencilIcon width={12} className="mr-2" /> Enter Payment
                  Details Manually
                </Flex>
              ),
            },
            ..._getPaymentDetailsList().map((v: any) => ({
              value: v.id,
              label: v.name,
            })),
          ]}
        />
      </AForm.Item>

      <AForm.Item dependencies={['destinationPaymentDetailsId']} noStyle>
        {() => {
          if (!form.getFieldValue('destinationPaymentDetailsId')) {
            return null;
          }

          if (transferCandidate.destinationNetwork === 'fiat') {
            return (
              <RecipientBankAccountForm
                transferCandidate={transferCandidate}
                form={form}
              />
            );
          }

          return (
            <RecipientWalletForm
              selectedDestinationPaymentDetails={_findPdsById(
                form.getFieldValue('destinationPaymentDetailsId')
              )}
              form={form}
            />
          );
        }}
      </AForm.Item>
      <StepFooter
        nextText={'Proceed to Payment'}
        previousText={'Cancel'}
        onPrevious={_handlePrev}
        onNext={_handleNext}
      />
    </AForm>
  );
};

const RecipientBankAccountForm = ({
  form,
  transferCandidate,
}: {
  form: FormInstance;
  transferCandidate: {
    sourceCurrency: string;
    sourceCurrencySymbol: string;
    sourceNetwork: string;
    sourceAmount: string;
    destinationCurrency: string;
    destinationCurrencySymbol: string;
    destinationNetwork: string;
    destinationAmount: string;
  };
}) => {
  const filterOption = (input: any, option: any) => {
    const label = option?.label;
    if (React.isValidElement(label)) {
      // If label is a React element, we need to access its text content
      // @ts-ignore
      const labelText = label.props.children
        .filter((child: any) => typeof child === 'string')
        .join(' ');
      return labelText.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    // Fallback for non-React element labels
    return String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  return (
    <>
      {transferCandidate.destinationCurrencySymbol === 'usd' && (
        <AForm.Item label="Transfer Method" name="bankTransferMethod">
          <Radio.Group size={'small'}>
            <Radio.Button value="ach">ACH</Radio.Button>
            <Radio.Button value="iban">IBAN</Radio.Button>
          </Radio.Group>
        </AForm.Item>
      )}

      <Flex gap={'middle'}>
        <AForm.Item
          className={'flex-1'}
          layout="vertical"
          rules={[
            {
              required: true,
              message: 'Enter beneficiary name',
            },
          ]}
          label="Beneficiary Name"
          name="accountBeneficiary">
          <Input placeholder={'e.g. "John Doe"'} />
        </AForm.Item>
        <AForm.Item
          className={'w-48'}
          rules={[
            {
              required: true,
              message: 'Select beneficiary type',
            },
          ]}
          name="bankAccountBeneficiaryType"
          layout="vertical"
          label="Beneficiary Type">
          <Select
            placeholder={'Select beneficiary type'}
            options={[
              {
                value: 'business',
                label: 'Business',
              },
              {
                value: 'individual',
                label: 'Individual',
              },
            ]}
          />
        </AForm.Item>
      </Flex>

      <AForm.Item dependencies={['bankTransferMethod']} noStyle>
        {() => {
          if (form.getFieldValue('bankTransferMethod') === 'ach') {
            return (
              <Flex gap={'middle'}>
                <AForm.Item
                  className={'flex-1'}
                  layout="vertical"
                  label="Account Number"
                  name="accountNumber"
                  rules={[
                    {
                      required: true,
                      message: 'Enter account number',
                    },
                  ]}>
                  <Input />
                </AForm.Item>
                <AForm.Item
                  className={'w-48'}
                  layout="vertical"
                  label="Routing Number"
                  rules={[
                    {
                      required: true,
                      message: 'Enter routing number',
                    },
                  ]}
                  name="accountACHRoutingNumber">
                  <Input />
                </AForm.Item>
              </Flex>
            );
          }

          return (
            <Flex gap={'middle'}>
              <AForm.Item
                className={'flex-1'}
                layout="vertical"
                label="Account IBAN"
                name="accountIBAN">
                <Input />
              </AForm.Item>
              <AForm.Item
                className={'w-48'}
                layout="vertical"
                label="BIC / Swift Code"
                name="bankBIC">
                <Input />
              </AForm.Item>
            </Flex>
          );
        }}
      </AForm.Item>

      <Flex gap={'middle'}>
        <AForm.Item
          className={'flex-1'}
          layout="vertical"
          label="Bank Name"
          rules={[
            {
              required: true,
              message: 'Enter bank name',
            },
          ]}
          name="bankName">
          <Input placeholder={'e.g. "Chase"'} />
        </AForm.Item>
        <AForm.Item
          className={'w-48'}
          layout="vertical"
          rules={[
            {
              required: true,
              message: 'Select bank country',
            },
          ]}
          label="Bank Country"
          name="bankAddressObj.countryAlpha2">
          <Select
            showSearch
            placeholder={'--'}
            filterOption={filterOption}
            // @ts-ignore
            rules={[
              {
                required: true,
                message: 'Select country',
              },
            ]}
            options={countries.map(({ label, value, flag }) => ({
              value,
              label: (
                <>
                  {flag} {label}
                </>
              ),
            }))}
          />
        </AForm.Item>
      </Flex>

      <Text className={'!text-indigo-300 block mb-6'}>Recipient Address:</Text>

      <Flex gap={'middle'}>
        <AForm.Item
          className={'flex-1'}
          layout="vertical"
          label="Country"
          rules={[
            {
              required: true,
              message: 'Select country',
            },
          ]}
          name="recipientAddress.countryAlpha2">
          <Select
            showSearch
            filterOption={filterOption}
            placeholder={'--'}
            options={countries.map(({ label, value, flag }) => ({
              value,
              label: (
                <>
                  {flag} {label}
                </>
              ),
            }))}
          />
        </AForm.Item>
        <AForm.Item
          className={'w-48'}
          layout="vertical"
          label="City"
          rules={[
            {
              required: true,
              message: 'Enter city',
            },
          ]}
          name="recipientAddress.city">
          <Input />
        </AForm.Item>
      </Flex>
      <Flex gap={'middle'}>
        <AForm.Item
          className={'flex-1'}
          layout="vertical"
          label="Address Line"
          rules={[
            {
              required: true,
              message: 'Enter street address',
            },
          ]}
          name="recipientAddress.streetAddress">
          <Input />
        </AForm.Item>
        <AForm.Item
          className={'w-48'}
          layout="vertical"
          label="Zip Code"
          rules={[
            {
              required: true,
              message: 'Enter postal/zip code',
            },
          ]}
          name="recipientAddress.postalCode">
          <Input />
        </AForm.Item>
      </Flex>
    </>
  );
};

const RecipientWalletForm = ({
  selectedDestinationPaymentDetails,
  form,
}: {
  form: FormInstance;
  selectedDestinationPaymentDetails: Record<string, any>;
}) => {
  const isManualFlow = !selectedDestinationPaymentDetails;

  if (isManualFlow) {
    return (
      <AForm.Item
        rules={[
          {
            required: true,
            message: 'Please enter valid wallet address',
          },
          {
            pattern: ethereumWalletRegex,
            message: 'Please enter valid wallet address',
          },
        ]}
        layout="vertical"
        name="walletAddress"
        label="Wallet Address">
        <Input placeholder="0x..." />
      </AForm.Item>
    );
  }

  return (
    <AForm.Item layout="vertical" label="Wallet Address">
      <Input
        disabled
        value={selectedDestinationPaymentDetails.details?.cryptoWallet?.address}
      />
    </AForm.Item>
  );
};

const StepFooter = ({
  nextText = 'Next',
  previousText = 'Previous',
  showPrevious = true,
  onNext,
  onPrevious,
}: {
  nextText?: string;
  previousText?: string;
  showPrevious?: boolean;
  onNext: () => void;
  onPrevious: () => void;
}) => {
  return (
    <Flex justify={'flex-end'} style={{ marginTop: 32 }} gap={'small'}>
      {showPrevious && (
        <AButton
          type="primary"
          onClick={onPrevious}
          className="w-36 bg-slate-700  hover:!bg-slate-600">
          {previousText}
        </AButton>
      )}

      <AButton
        type="primary"
        onClick={onNext}
        className="bg-indigo-600  hover:!bg-indigo-500">
        {nextText}
      </AButton>
    </Flex>
  );
};
